import React, { useEffect, useMemo, useState } from 'react';
import { Settings2 } from 'lucide-react';
import { Input } from './Input';
import { PhoneField } from './PhoneField';
import { Textarea } from './Textarea';
import { Select } from './Select';
import { FormField } from './FormField';
import { customFieldsService, CustomFieldDefinition, CustomFieldEntityType } from '@/services/custom-fields.service';

interface Props {
  entityType: CustomFieldEntityType;
  /** Current values keyed by field.key. */
  values: Record<string, unknown>;
  /** Called whenever a field is blurred/changed; caller persists. */
  onChange: (key: string, value: unknown) => void;
  /** Called when ALL field values should be saved (caller decides when). Optional. */
  onSave?: (values: Record<string, unknown>) => Promise<void>;
}

/**
 * Renders a form for the company's custom-field definitions for the given
 * entity type. The form is data-driven: caller owns the values record + decides
 * how/when to persist. This widget handles validation hints + UI rendering.
 */
export const CustomFieldsForm: React.FC<Props> = ({ entityType, values, onChange }) => {
  const [defs, setDefs] = useState<CustomFieldDefinition[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    customFieldsService.list(entityType)
      .then((list) => { if (!cancelled) setDefs(list.filter((d) => d.isActive)); })
      .catch(() => { if (!cancelled) setDefs([]); });
    return () => { cancelled = true; };
  }, [entityType]);

  const sorted = useMemo(() => (defs ?? []).sort((a, b) => a.order - b.order), [defs]);

  if (defs === null) {
    return <div className="skeleton h-24 rounded-lg" />;
  }

  if (defs.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-xs text-text-muted">
        <Settings2 size={20} className="mx-auto mb-1 opacity-50" />
        No custom fields defined for {entityType}s yet. Define them in Settings → Custom fields.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {sorted.map((f) => {
        const val = values[f.key];
        return (
          <FormField key={f.fieldUid} label={f.name} required={f.required} hint={f.helpText}>
            {renderField(f, val, (v) => onChange(f.key, v))}
          </FormField>
        );
      })}
    </div>
  );
};

function renderField(f: CustomFieldDefinition, val: unknown, set: (v: unknown) => void): React.ReactNode {
  const str = (val ?? f.defaultValue ?? '') as string;
  switch (f.kind) {
    case 'phone':
      return (
        <PhoneField
          value={str}
          placeholder={f.placeholder}
          onChange={(e164) => set(e164)}
        />
      );
    case 'text':
    case 'url':
    case 'email':
      return (
        <Input
          type={f.kind === 'email' ? 'email' : f.kind === 'url' ? 'url' : 'text'}
          value={str}
          placeholder={f.placeholder}
          onChange={(e) => set(e.target.value)}
        />
      );
    case 'long_text':
      return <Textarea rows={3} value={str} placeholder={f.placeholder} onChange={(e) => set(e.target.value)} />;
    case 'number':
    case 'currency':
      return (
        <Input
          type="number"
          value={str}
          placeholder={f.placeholder ?? (f.kind === 'currency' ? '0.00' : '')}
          onChange={(e) => set(e.target.value === '' ? '' : Number(e.target.value))}
        />
      );
    case 'date':
      return <Input type="date" value={(str ?? '').slice(0, 10)} onChange={(e) => set(e.target.value)} />;
    case 'datetime':
      return <Input type="datetime-local" value={(str ?? '').slice(0, 16)} onChange={(e) => set(e.target.value)} />;
    case 'boolean':
      return (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-border"
            checked={!!val}
            onChange={(e) => set(e.target.checked)}
          />
          <span className="text-text-secondary">{f.placeholder ?? 'Yes'}</span>
        </label>
      );
    case 'dropdown':
      return (
        <Select value={str} onChange={(e) => set(e.target.value)}>
          <option value="">{f.placeholder ?? '— Select —'}</option>
          {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </Select>
      );
    case 'multi_select': {
      const arr: string[] = Array.isArray(val) ? (val as string[]) : (typeof val === 'string' && val ? val.split(',') : []);
      return (
        <div className="flex flex-wrap gap-1.5">
          {f.options.map((o) => {
            const checked = arr.includes(o);
            return (
              <label
                key={o}
                className={`inline-flex cursor-pointer items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition ${
                  checked ? 'border-primary bg-primary-soft text-primary' : 'border-border text-text-secondary hover:bg-surface-alt'
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    const next = e.target.checked ? [...arr, o] : arr.filter((x) => x !== o);
                    set(next);
                  }}
                  className="hidden"
                />
                {o}
              </label>
            );
          })}
        </div>
      );
    }
    default:
      return <Input value={str} onChange={(e) => set(e.target.value)} />;
  }
}
